import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import {
  DateRange,
  FiltersDefinition,
  FiltersState,
  OneDataCollection,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import {
  AnalyticsSnapshotResultsDocument,
  AnalyticsSnapshotResultsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { datatableFiltersToJsonLogicFilters } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { formatColumns } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

import type { ReportTableAction, ReportTableProps } from './index'

interface SnapshotTableContentProps {
  id: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ReportTableProps['columnFormatHints']
  onStateChange: ReportTableProps['onStateChange']
  dateFilter?: DateRange
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  columnsData: AnalyticsSnapshotResultsQuery
  filterDefinitions: FiltersDefinition<string>
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const SnapshotTableContent = ({
  id,
  sqlQuery,
  tableId,
  actions,
  columnFormatHints,
  onStateChange,
  dateFilter,
  onUserFacingSqlQueryChange,
  columnsData,
  filterDefinitions,
  currentFilters,
}: SnapshotTableContentProps) => {
  const client = useApolloClient()
  const { company, access } = useCurrentSession()

  const source = useDataCollectionSource(
    {
      filters: filterDefinitions,
      currentFilters: currentFilters,
      primaryActions: () => actions?.primary,
      secondaryActions: actions?.secondary
        ? {
            expanded: 1,
            actions: () => actions?.secondary ?? [],
          }
        : undefined,
      dataAdapter: {
        paginationType: 'pages',
        perPage: INITIAL_PAGE_SIZE,
        fetchData: (options) => {
          const pagination = options.pagination
          const perPage = pagination.perPage ?? INITIAL_PAGE_SIZE
          const currentPage = pagination.currentPage ?? INITIAL_PAGE

          const snapshotDate = dateFilter?.from ?? new Date()

          const filters = options.filters
          const jsonLogicFilters = datatableFiltersToJsonLogicFilters(filters)

          return client
            .watchQuery({
              query: AnalyticsSnapshotResultsDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                query: sqlQuery,
                companyId: company.id,
                snapshotDate: snapshotDate.toISOString(),
                page: currentPage,
                perPage,
                tableId,
                filters: JSON.stringify(jsonLogicFilters),
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              onUserFacingSqlQueryChange(
                data?.iris?.snapshotResultsConnection?.nodes?.[0]?.sqlQuery ?? ''
              )
              return {
                loading: loading,
                error: error,
                data: {
                  records:
                    data?.iris.snapshotResultsConnection.nodes.map((node) =>
                      Object.fromEntries(node.values.map(({ name, value }) => [name, value ?? '']))
                    ) ?? [],
                  total: data?.iris.snapshotResultsConnection.totalCount ?? 0,
                  currentPage: currentPage,
                  pagesCount: Math.ceil(
                    (data?.iris.snapshotResultsConnection.totalCount ?? 0) / perPage
                  ),
                  perPage: perPage,
                  type: 'pages',
                },
              }
            })
        },
      },
    },
    [sqlQuery, company.id, dateFilter, filterDefinitions]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      id={`snapshot-table-${id}/v1`}
      storage={false}
      onStateChange={onStateChange}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnReordering: true,
            allowColumnHiding: true,
            frozenColumns: 1,
            columns: formatColumns(
              columnsData?.iris.snapshotResultsConnection.nodes || [],
              columnFormatHints,
              access.locale
            ),
          },
        },
      ]}
    />
  )
}

export default SnapshotTableContent
